/* eslint-disable arrow-body-style */
import React, { useCallback, useMemo, useState, memo, useRef } from 'react';
import { Menu, Bubble } from 'tea-component';
import Folder from '@/pages/dashboard/components/LeftMenu/components/Menu/Folder';
import SvgIcon from '@home/components/SvgIcon';
import MenuPopover from '@/pages/dashboard/components/LeftMenu/components/MenuPopover';
import FolderPopover from '@/pages/dashboard/components/LeftMenu/components/FolderPopover';
import SubFolderPopover from '@/pages/dashboard/components/LeftMenu/components/SubFolderPopover';
import DashboardPopover from '@/pages/dashboard/components/LeftMenu/components/DashboardPopover';
import classNames from 'classnames';
import {
  MENU_OPTION_TYPE_DASHBOARD,
  MENU_OPTION_TYPE_FOLDER,
  MENU_OPTION_TYPE_MENU, MENU_OPTION_TYPE_SUB_FOLDER,
  MenuContext
} from '@/pages/dashboard/components/LeftMenu/components/Menu/config';
import { BUSINESS_TYPE_MYDASHBOARD, FOLDER_NAME_FAVORITES, SYS_ADMIN } from '@/pages/dashboard/_config/constant';
import { useSelector } from 'umi';
import './index.scss';

type IMenuProps = {
  menuList: Array<any>;
  setModalInfoState: React.SetStateAction<any>;
  showNewMenusMap: any;
}

const MenuProvider = MenuContext.Provider;

// 左侧菜单结构：menu-folder-subfolder-dashboard
export default memo((props: IMenuProps) => {
  const isSettingBubbleRef = useRef<boolean>(false);
  const { menuList = [], setModalInfoState, showNewMenusMap } = props;
  const [bubbleInfo, setBubbleInfo] = useState<null | {ele:  Element; content: string;}>(null);
  const [menuOptionInfo, setMenuOptionInfo] = useState<{type: string; visible: boolean; ele:  EventTarget | null; menu: any; parent?: any;}>({
    // 类型
    type: '',
    // 显影
    visible: false,
    // 参考定位的元素，referenceElement
    ele: null,
    // 当前菜单数据
    menu: null,
    parent: null
  });

  const showBubble = useCallback((
    callback: () => ({ ele: Element; content: string; } | undefined),
  ): void => {
    // 更新状态为“正在设置气泡”
    isSettingBubbleRef.current = true;
    const { ele, content } = callback() || {};
    if (ele && content) setBubbleInfo({ ele, content });

    // 更新状态为“未正在设置气泡”
    setTimeout(() => {
      isSettingBubbleRef.current = false;
    }, 0);
  }, []);

  const hideBubble = useCallback(() => {
    // 如果正在设置气泡，则直接返回
    if (isSettingBubbleRef.current) return;
    setBubbleInfo(null);
  }, []);

  // 开启左侧操作栏
  const openMenuOption = useCallback((type: string, menu: any, ele: EventTarget, parent?: any): void => {
    setMenuOptionInfo({
      type,
      visible: true,
      ele,
      menu,
      parent
    });
  }, []);

  // 修改左侧操作栏显影
  const changeMenuOption = useCallback((visible: boolean): void => {
    setMenuOptionInfo({
      ...menuOptionInfo,
      visible,
    });
  }, [menuOptionInfo]);

  const { currentGame } = useSelector((state: any) => {
    return {
      currentGame: state.dashboard.currentGame
    };
  });
  const { role_id: roleId = '' } = currentGame || {};
  const isHavePermission = roleId === SYS_ADMIN;

  //
  const moveToList: Array<any> = useMemo(() => {
    const { visible, type, menu } = menuOptionInfo;
    if (!visible || type === MENU_OPTION_TYPE_MENU) {
      return [];
    }
    // 一级folder 移动到其他menu下面
    if (type === MENU_OPTION_TYPE_FOLDER) {
      return menuList;
    }
    // 二级folder，当前menu的其他一级目录下
    if (type === MENU_OPTION_TYPE_SUB_FOLDER) {
      return menuList.find(item => item.id === menu?.menu_id)?.folder_list || [];
    }

    // dashboard 可移动到当前menu的任意目录
    if (type === MENU_OPTION_TYPE_DASHBOARD) {
      return menuList.find(item => item.id === menu?.menu_id)?.folder_list || [];
    }
    return [];
  }, [menuOptionInfo, menuList]);

  // 已收藏的游戏，用来对每个报表的左侧弹窗做收藏和取消收藏的操作
  const favoritesDashboardData = useMemo(() => {
    // 和后端确认过，每一层，business_type === "mydashboard"的数据最多只有一个
    return menuList?.filter((menuItem: { business_type: string; }) => {
      return menuItem?.business_type === BUSINESS_TYPE_MYDASHBOARD;
    })?.[0]?.folder_list?.filter(((items: { business_type: string; name_en: string; }) =>
      items.business_type === BUSINESS_TYPE_MYDASHBOARD && items?.name_en === FOLDER_NAME_FAVORITES))?.[0]?.dashoboard_list || [];
  }, [menuList]);

  const menuContextState = useMemo(() => {
    return {showBubble, hideBubble};
  }, [showBubble, hideBubble]);

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, content: string): void => {
    const titleEl = e.currentTarget.getElementsByClassName('tea-menu__text')[0];
    if (titleEl && titleEl.scrollHeight > titleEl.clientHeight) {
      const anchor = e.currentTarget.getElementsByClassName('tea-menu__item')[0];
      showBubble(() => ({ ele: anchor || e.currentTarget, content }));
    }
  };

  const handleMouseLeave = (): void => {
    hideBubble();
  };

  return <MenuProvider value={menuContextState}>
    <Menu>
      {menuList.map((menu: any) => {
        return <Menu.Group
          // @ts-ignore
          onMouseEnter={e => handleMouseEnter(e, menu.text)}
          onMouseLeave={handleMouseLeave}
          title={<>
            {menu.text}
            {
              isHavePermission && <span className='left-nav-absolute' onClick={(e) => {openMenuOption(MENU_OPTION_TYPE_MENU, menu, e.target)}}> <SvgIcon type="more"/></span>
            }
          </>}
          className={classNames({
            'left-nav-group dbh-left-nav-ellipsis-with-tooltip': true,
            'active': menuOptionInfo.visible && menuOptionInfo.menu?.id === menu.id
          })}
          key={menu?.id}
        >
          {(menu?.folder_list || []).map((folder: any) => {
            return <Folder
              parent={menu}
              folder={folder}
              key={folder?.id}
              menuOptionInfo={menuOptionInfo}
              openMenuOption={openMenuOption}
              showNewMenusMap={showNewMenusMap}
            />;
          })}
        </Menu.Group>;
      })}
    </Menu>
    {menuOptionInfo.type === MENU_OPTION_TYPE_MENU && menuOptionInfo.visible && <MenuPopover
      visible={menuOptionInfo.visible}
      referenceElement={menuOptionInfo.ele}
      menu={menuOptionInfo.menu}
      setVisible={changeMenuOption}
      setModalInfoState={setModalInfoState}
    />}

    {menuOptionInfo.type === MENU_OPTION_TYPE_FOLDER && menuOptionInfo.visible && <FolderPopover
      menu={menuOptionInfo?.parent || []}
      visible={menuOptionInfo.visible}
      referenceElement={menuOptionInfo.ele}
      item={menuOptionInfo.menu}
      setVisible={changeMenuOption}
      setModalInfoState={setModalInfoState}
      moveToMenu={moveToList}
    />}

    {menuOptionInfo.type === MENU_OPTION_TYPE_SUB_FOLDER && menuOptionInfo.visible && <SubFolderPopover
      folder={menuOptionInfo?.parent || []}
      visible={menuOptionInfo.visible}
      referenceElement={menuOptionInfo.ele}
      item={menuOptionInfo.menu}
      setVisible={changeMenuOption}
      setModalInfoState={setModalInfoState}
      moveToMenu={moveToList}
    />}

    {menuOptionInfo.type === MENU_OPTION_TYPE_DASHBOARD && menuOptionInfo.visible && <DashboardPopover
      folder={menuOptionInfo?.parent || []}
      visible={menuOptionInfo.visible}
      referenceElement={menuOptionInfo.ele}
      item={menuOptionInfo.menu}
      setVisible={changeMenuOption}
      setModalInfoState={setModalInfoState}
      moveToMenu={moveToList}
      favoritesDashboardList={favoritesDashboardData}
    />}
    {bubbleInfo && <Bubble visible={true} dark placement='right' referenceElement={bubbleInfo?.ele} content={bubbleInfo?.content} />}
  </MenuProvider>;
});
